import { useState } from 'react';
import { usePageContext } from 'context/hooks/usePageContext';
import Button from 'components/ui/Button/Button';
import FloatingLabelInput from 'components/ui/Inputs/FloatingLabelInput';
import { validatePhone } from 'utils/validation';
import axiosInstance from 'utils/axiosInstance';
import { checkResponse, getResponseMessage } from 'utils';
import { useTransactionContext } from 'context/hooks/useTransactionContext';
import { useCardContext } from 'context/hooks/useCardContext';
import useGetCardPaymentStatus from 'hooks/useGetCardPaymentStatus';
import { PAGE_SUB } from 'context/pageContext';
import { getApiResponseMessage } from 'services/paymentHandlers/apiError';

const PhoneAuth = ({
  setUrl,
}: {
  setUrl: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const { ceviantTransactionId } = useTransactionContext();
  const { changeSub, changePage } = usePageContext();
  const { changeCardDetails } = useCardContext();
  const [phoneNumber, setPhoneNumber] = useState({ phone: '' });
  const [phoneErrors, setPhoneErrors] = useState<Record<string, string>>({});
  const [isPhoneSubmitting, setIsPhoneSubmitting] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);

  const { triggerFetch, error } = useGetCardPaymentStatus(
    ceviantTransactionId,
    {
      shouldFetch,
    },
    `/payments/status`
  );

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber({ ...phoneNumber, [e.target.name]: e.target.value });
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validatePhone(phoneNumber);
    if (validation.isValid) {
      try {
        setIsPhoneSubmitting(true);
        const payload = {
          phone: phoneNumber.phone,
          reference: ceviantTransactionId,
        };

        const response = await axiosInstance.post(
          '/payments/phoneAuths',
          payload
        );

        if (response.data) {
          if (response.data.url) {
            setUrl(response.data.url);
          }
          changeCardDetails({
            otpPrompt: response.data.metadata?.displayText ?? '',
          });
          checkResponse(
            response.data.response,
            changeSub,
            changePage,
            getResponseMessage(response.data),
          );
        }
      } catch (error) {
        // The API rejected the request and said why (e.g. "Invalid OTP"),
        // so show that rather than polling a status that hides it.
        const apiMessage = getApiResponseMessage(error);
        if (apiMessage) {
          changeSub(PAGE_SUB.ERROR, apiMessage);
          return;
        }

        setShouldFetch(true);
        triggerFetch();
      } finally {
        setIsPhoneSubmitting(false);
      }
    } else {
      setPhoneErrors(validation.errors);
    }
  };

  if (error) {
    changeSub(PAGE_SUB.ERROR);
  }

  return (
    <div className="space-y-6 text-center py-14">
      <img
        src="/icons/authenticate.svg"
        alt=""
        className="m-auto w-11"
      />
      <h3 className="text-[#535353] text-lg font-semibold">
        Please input your phone number to continue your payment
      </h3>
      <form
        className="flex flex-col gap-5 px-4"
        onSubmit={handleSendCode}
      >
        <FloatingLabelInput
          label="PHONE NUMBER"
          type="number"
          pattern="[0-9]*"
          inputMode="numeric"
          value={phoneNumber.phone}
          onChange={handlePhoneChange}
          name="phone"
        />
        {phoneErrors.otp && (
          <p className="text-red-500 text-sm mt-1">{phoneErrors.otp}</p>
        )}
        <div className="flex gap-3 w-full m-auto">
          <Button
            type="submit"
            variant="primary"
            disabled={isPhoneSubmitting}
          >
            Authenticate
          </Button>
          <Button
            variant="outline"
            type="button"
            onClick={() =>
              window.parent.postMessage({ message: 'closeCeviantSDK' }, '*')
            }
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PhoneAuth;
